import React, { useEffect, useState } from "react";
import {
  PaginatedGridLayout,
  ParticipantView,
  useCall,
  useCallStateHooks,
} from "@stream-io/video-react-sdk";
import backgroundImage from "../../../../assets/svg/ChatBG.svg";
import ChatComponent from "../../../../components/Chat/ChatComponent";
import GridIcon from "../../../../assets/svg/GridIcon.svg";
import ListIcon from "../../../../assets/svg/ListIcon.svg";
import ToggleSwitch from "../../../../components/reusable/ToggleSwitch";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInformations } from "../../../../features/authSlice";
import { hasRole, showToast } from "../../../../utils/utils";
import axios from "axios";
import { API } from "../../../../shared/api/Api";
import {
  selectCurrentLive,
  setChatEnabledState,
} from "../../../../features/live/liveSlice";
import Loader from "../../../../components/Live/Loader";
import { ReactComponent as Raisehand } from "../../../../assets/svg/RaisHand.svg";

const ChatBox = ({ room_uuid }) => {
  const [activeTab, setActiveTab] = useState("Participants");
  const [viewType, setViewType] = useState("grid");
  const [loader, setLoader] = useState(false);
  const { useParticipants, useParticipantCount } = useCallStateHooks();
  const participants = useParticipants();
  const participantCount = useParticipantCount();
  const user = useSelector(selectUserInformations);
  const currentLive = useSelector(selectCurrentLive);
  const [chatEnabled, setChatEnabled] = useState(
    currentLive?.room?.chat_enabled
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setChatEnabled(currentLive?.room?.chat_enabled);
  }, [currentLive?.room?.chat_enabled]);

  const handleChangeChatActivation = async (room_uuid) => {
    setChatEnabled(!chatEnabled);
    try {
      setLoader(true);
      const { data } = await axios.put(
        `${API.Courses.app_courses_chat_changeRoomStatus}/${room_uuid}`,
        { enabled: !chatEnabled },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        dispatch(setChatEnabledState(data.data.chat_enabled));
        showToast("Chat Activation", data.message, "success");
      } else {
        showToast(
          "Oops, Something went wrong",
          "There is an error, please try again",
          "error"
        );
      }
    } catch (error) {
      console.log(error);
      showToast(
        "Oops, Something went wrong",
        "There is an error, please try again",
        "error"
      );
    } finally {
      setLoader(false);
    }
  };

  return (
    <div
      className="h-full pt-2 ml:pt-3 ll:pt-4 mx-auto border rounded-xl shadow-xl flex flex-col w-full"
      style={
        activeTab === "Chat"
          ? {
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
          : {}
      }
    >
      {/* Tabs for Chat and Participants */}
      <div className="flex flex-row justify-between w-full ll:text-base sl:text-xs ml:text-sm font-normal px-4 ml:px-10 ll:px-12 p-1 sl:p-0 ll:p-2">
        {["Participants", "Chat"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-1 ml:py-3 px-3 ml:px-6 sl:px-6 ${activeTab === tab
              ? "text-[#134F98] border-b-4 rounded-sm border-[#134F98]"
              : "text-[#434343]"
              }`}
          >
            {tab === "Participants" && `Participants (${participantCount - 1})`}
            {tab === "Chat" && (
              <div className="flex items-center space-x-1 ml:space-x-2">
                <span>Chat</span>
                {(user.instructor !== undefined ||
                  hasRole(user?.roles, "super-admin")) && (
                    <>
                      {activeTab === "Chat" && (
                        <div>
                          {loader ? (
                            <Loader />
                          ) : (
                            <ToggleSwitch
                              checked={chatEnabled}
                              onChange={() =>
                                handleChangeChatActivation(room_uuid)
                              }
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          </button>
        ))}
      </div>

      {activeTab === "Chat" ? (
        <ChatComponent room_uuid={room_uuid} chatEnabled={chatEnabled} />
      ) : (
        <div className="flex flex-col items-center mx-auto h-full w-full max-w-[27.9375rem] max-h-[45vh] ml:max-h-[55vh] ll:max-h-[60vh]">
          {participants.filter(participant => participant.userId !== user.uuid).length <= 0 && (
            <div className="flex justify-center items-center">
              <span className="text-md ll:text-lg text-primary">
                There are no participants
              </span>
            </div>
          )}
          {/* Participant View */}
          <div className="flex flex-col items-center mx-auto h-full w-full max-h-[45vh] ml:max-h-[55vh] ll:max-h-[60vh] overflow-y-auto pt-1 px-2 ml:p-4 ll:px-6 overflow-x-hidden">
            {viewType === "grid" ? (
              <div className="grid grid-cols-2 gap-3 w-full h-[320px] overflow-y-auto px-2 py-2">
                {participants
                  .filter((participant) => participant.userId !== user.uuid) // Exclude local participant
                  .map((participant, index) => (
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden shadow-sm h-[130px]"
                    >
                      <ParticipantView
                        participant={participant}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  ))}
              </div>
            ) : (
              <div className="w-full h-[320px]">
                {participants
                  .filter((participant) => participant?.userId !== user.uuid) // Exclude local participant
                  .map((participant, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between w-full p-2 sl:p-3 ml:p-4 ll:p-5 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-start items-center space-x-2 sl:space-x-3 ml:space-x-4">
                        <div className="w-8 h-8 sl:w-10 sl:h-10 ml:w-12 ml:h-12 ll:w-14 ll:h-14 rounded-full overflow-hidden bg-gray-100">
                          {participant?.image ? (
                            <img
                              className="w-full h-full object-cover"
                              src={
                                participant?.image ||
                                "https://via.placeholder.com/40"
                              }
                              alt={participant?.name || "Participant"}
                            />
                          ) : (
                            <ParticipantView
                              participant={participant}
                              className="w-full h-full object-cover"
                              trackType="videoTrack"
                            />
                          )}
                        </div>
                        <span className="text-xs sl:text-sm ml:text-base ll:text-lg font-normal text-[#434343]">
                          {participant?.name ||
                            participant?.userId ||
                            "Unknown User"}
                        </span>
                      </div>
                      {participant?.reaction?.emoji_code === ":raise-hand:" && (
                        <Raisehand className="text-white w-5 h-5 sl:w-6 sl:h-6 ml:w-7 ml:h-7 ll:w-8 ll:h-8 mr-2" />
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
          {/* Fixed View Toggle */}
          <div className="sticky bottom-0 w-full flex justify-center items-center border-t-2 bg-white rounded-b-xl shadow-lg">
            <div className="flex w-full max-w-[447px]">
              <button
                onClick={() => setViewType("grid")}
                className={`flex items-center justify-center w-1/2 py-4 md:py-6 space-x-2 rounded-b-xl ${viewType === "grid"
                  ? "bg-[#E7E7E7] text-[#134F98] font-semibold border-b-4 rounded-br-none"
                  : "bg-white text-[#434343]"
                  }`}
              >
                <img
                  className="w-5 h-5 md:w-4 md:h-4 lg:w-5 lg:h-5"
                  src={GridIcon}
                  alt="Grid View Icon"
                />
                <span className="text-[12px] md:text-[15px] ml:text-[15px] ll:text-[20px] text-center">
                  Grid View
                </span>
              </button>

              <button
                onClick={() => setViewType("list")}
                className={`flex items-center justify-center w-1/2 py-4 md:py-6 space-x-2 rounded-b-xl ${viewType === "list"
                  ? "bg-[#E7E7E7] text-[#134F98] font-semibold border-b-4 rounded-bl-none"
                  : "bg-white text-[#434343]"
                  }`}
              >
                <img
                  className="w-5 h-5 md:w-4 md:h-4 lg:w-5 lg:h-5"
                  src={ListIcon}
                  alt="List View Icon"
                />
                <span className="text-[12px] md:text-[15px] ml:text-[15px] ll:text-[20px] text-center">
                  List View
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBox;